import { baseReducer } from '../core/base-reducer';
import * as Actions from './customs-documents.actions';
import type { CustomsDocument } from './types';

const MAX_RETAINED_FAILED_DOCS = 5;

const initialState = {
  capabilities: null,
  isLoadingCapabilities: false,
  capabilitiesError: null,

  // { [salesOrderId]: { documents: [], isLoading: bool, isUploading: bool, error: null } }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  orderDocuments: {} as Record<string, any>,

  // { [shipmentId]: { documents: [], isLoading: bool, isUploading: bool, isSending: bool, error: null } }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shipmentDocuments: {} as Record<string, any>,

  // { [fulfillmentPlanId]: boolean } -- optimistic, survives CSW state overwrites
  pendingDocuments: {} as Record<string, boolean>,
};

const initialOrderState = {
  documents: [],
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  error: null,
};

const initialShipmentState = {
  documents: [],
  isLoading: false,
  isUploading: false,
  isSending: false,
  error: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const actionHandlerMap: Record<string, (state: typeof initialState, action: any) => typeof initialState> = {
  // ── Carrier Capabilities ──────────────────────────────────────────────────
  [Actions.GET_CARRIER_DOCUMENT_CAPABILITIES_REQUEST]: (state) => ({
    ...state,
    isLoadingCapabilities: true,
    capabilitiesError: null,
  }),

  [Actions.GET_CARRIER_DOCUMENT_CAPABILITIES_SUCCESS]: (state, action) => ({
    ...state,
    isLoadingCapabilities: false,
    capabilities: action.capabilities,
  }),

  [Actions.GET_CARRIER_DOCUMENT_CAPABILITIES_FAILURE]: (state, action) => ({
    ...state,
    isLoadingCapabilities: false,
    capabilitiesError: action.error,
  }),

  // ── Order Documents ───────────────────────────────────────────────────────
  [Actions.GET_ORDER_DOCUMENTS_REQUEST]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: { ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState), isLoading: true, error: null },
    },
  }),

  [Actions.GET_ORDER_DOCUMENTS_SUCCESS]: (state, action) => {
    const existingOrderDocs = state.orderDocuments[action.salesOrderId]?.documents ?? [];
    const localFailedDocs = existingOrderDocs
      .filter((doc: CustomsDocument) => doc.documentId?.startsWith('failed-'))
      .slice(-MAX_RETAINED_FAILED_DOCS);
    return {
      ...state,
      orderDocuments: {
        ...state.orderDocuments,
        [action.salesOrderId]: {
          ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState),
          isLoading: false,
          documents: [...action.documents, ...localFailedDocs],
        },
      },
    };
  },

  [Actions.GET_ORDER_DOCUMENTS_FAILURE]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: {
        ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState),
        isLoading: false,
        error: action.error,
      },
    },
  }),

  [Actions.UPLOAD_ORDER_DOCUMENT_REQUEST]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: { ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState), isUploading: true, error: null },
    },
  }),

  [Actions.UPLOAD_ORDER_DOCUMENT_SUCCESS]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: {
        ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState),
        documents: [...(state.orderDocuments[action.salesOrderId]?.documents ?? []), action.document],
      },
    },
  }),

  [Actions.UPDATE_ORDER_UPLOAD_PROGRESS]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: {
        ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState),
        uploadProgress: action.percentageComplete,
      },
    },
  }),

  [Actions.UPLOAD_ORDER_DOCUMENT_FILE_UPLOADED]: (state, action) => {
    const prevOrderDocs = state.orderDocuments[action.salesOrderId] ?? initialOrderState;
    return {
      ...state,
      orderDocuments: {
        ...state.orderDocuments,
        [action.salesOrderId]: {
          ...prevOrderDocs,
          isUploading: false,
          uploadProgress: 0,
          documents: prevOrderDocs.documents.map((doc: CustomsDocument) =>
            doc.documentId === action.documentId ? { ...doc, status: 'uploaded' } : doc,
          ),
        },
      },
    };
  },

  [Actions.UPLOAD_ORDER_DOCUMENT_FAILURE]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: {
        ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState),
        isUploading: false,
        uploadProgress: 0,
        error: action.error,
      },
    },
  }),

  [Actions.DELETE_ORDER_DOCUMENT_SUCCESS]: (state, action) => ({
    ...state,
    orderDocuments: {
      ...state.orderDocuments,
      [action.salesOrderId]: {
        ...(state.orderDocuments[action.salesOrderId] ?? initialOrderState),
        documents: (state.orderDocuments[action.salesOrderId]?.documents ?? []).filter(
          (doc: CustomsDocument) => doc.documentId !== action.documentId,
        ),
      },
    },
  }),

  // ── Shipment Documents ────────────────────────────────────────────────────
  [Actions.GET_SHIPMENT_DOCUMENTS_REQUEST]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: { ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState), isLoading: true, error: null },
    },
  }),

  [Actions.GET_SHIPMENT_DOCUMENTS_SUCCESS]: (state, action) => {
    const existingShipDocs = state.shipmentDocuments[action.shipmentId]?.documents ?? [];
    const localFailedShipDocs = existingShipDocs
      .filter((doc: CustomsDocument) => doc.documentId?.startsWith('failed-'))
      .slice(-MAX_RETAINED_FAILED_DOCS);
    return {
      ...state,
      shipmentDocuments: {
        ...state.shipmentDocuments,
        [action.shipmentId]: {
          ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
          isLoading: false,
          documents: [...action.documents, ...localFailedShipDocs],
        },
      },
    };
  },

  [Actions.GET_SHIPMENT_DOCUMENTS_FAILURE]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: {
        ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
        isLoading: false,
        error: action.error,
      },
    },
  }),

  [Actions.UPLOAD_SHIPMENT_DOCUMENT_REQUEST]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: {
        ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
        isUploading: true,
        error: null,
      },
    },
  }),

  [Actions.UPLOAD_SHIPMENT_DOCUMENT_SUCCESS]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: {
        ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
        documents: [...(state.shipmentDocuments[action.shipmentId]?.documents ?? []), action.document],
      },
    },
  }),

  [Actions.UPLOAD_SHIPMENT_DOCUMENT_FILE_UPLOADED]: (state, action) => {
    const prevShipDocs = state.shipmentDocuments[action.shipmentId] ?? initialShipmentState;
    return {
      ...state,
      shipmentDocuments: {
        ...state.shipmentDocuments,
        [action.shipmentId]: {
          ...prevShipDocs,
          isUploading: false,
          documents: prevShipDocs.documents.map((doc: CustomsDocument) =>
            doc.documentId === action.documentId ? { ...doc, status: 'uploaded' } : doc,
          ),
        },
      },
    };
  },

  [Actions.UPLOAD_SHIPMENT_DOCUMENT_FAILURE]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: {
        ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
        isUploading: false,
        error: action.error,
      },
    },
  }),

  [Actions.DELETE_SHIPMENT_DOCUMENT_SUCCESS]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: {
        ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
        documents: (state.shipmentDocuments[action.shipmentId]?.documents ?? []).filter(
          (doc: CustomsDocument) => doc.documentId !== action.documentId,
        ),
      },
    },
  }),

  [Actions.SEND_SHIPMENT_DOCUMENTS_REQUEST]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: { ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState), isSending: true, error: null },
    },
  }),

  [Actions.SEND_SHIPMENT_DOCUMENTS_SUCCESS]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: { ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState), isSending: false },
    },
  }),

  [Actions.SEND_SHIPMENT_DOCUMENTS_FAILURE]: (state, action) => ({
    ...state,
    shipmentDocuments: {
      ...state.shipmentDocuments,
      [action.shipmentId]: {
        ...(state.shipmentDocuments[action.shipmentId] ?? initialShipmentState),
        isSending: false,
        error: action.error,
      },
    },
  }),

  // ── Pending Document Config ─────────────────────────────────────────────
  [Actions.SAVE_PENDING_DOCUMENT_CONFIG_REQUEST]: (state, action) => ({
    ...state,
    pendingDocuments: { ...state.pendingDocuments, [action.fulfillmentPlanId]: action.pendingDocument },
  }),
};

export const customsDocumentsReducer = baseReducer(initialState, actionHandlerMap);
